import { useState, useEffect } from 'react';
import { useTheme } from 'next-themes';
import { configService } from '@/services/config-service';

interface EvoFeedbackUrlParams {
  feature: string;
  customerName?: string;
  userId?: string;
  roleId?: string;
  language?: string;
}

/**
 * Custom hook to build the Evo feedback collector URL with all required parameters.
 * This version includes theme support and additional Evo-specific parameters.
 *
 * @param params - The parameters for the Evo feedback collector
 * @returns The complete URL for the oc-component, or null if config is not loaded yet
 */
export function useEvoFeedbackCollectorUrl(params: EvoFeedbackUrlParams): string | null {
  const { feature, customerName, userId, roleId, language } = params;
  const { resolvedTheme } = useTheme();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function buildUrl(): Promise<void> {
      try {
        const config = await configService.getFeedbackCollectorConfig();

        if (!isMounted) return;

        const urlParams = new URLSearchParams();
        urlParams.set('product', config.product);
        urlParams.set('feature', feature);
        urlParams.set('accountToken', config.accountToken);
        urlParams.set('customerName', customerName ?? 'Unknown');
        urlParams.set('userId', userId ?? 'Unknown');
        urlParams.set('roleId', roleId ?? 'Unknown');
        urlParams.set('language', language ?? 'en-GB');
        urlParams.set('theme', resolvedTheme === 'dark' ? 'dark' : 'light');

        const fullUrl = `${config.baseUrl}?${urlParams.toString()}`;
        setUrl(fullUrl);
      } catch (error) {
        console.error('Failed to build Evo feedback collector URL:', error);
        setUrl(null);
      }
    }

    buildUrl();

    return () => {
      isMounted = false;
    };
  }, [feature, customerName, userId, roleId, language, resolvedTheme]);

  return url;
}
